import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, Server, AlertCircle, KeyRound, Tag, Info, Globe, Lock, Check, Loader2, ExternalLink } from 'lucide-react';
import { useCreateMt5Account, useMt5Accounts } from '@/hooks/use-accounts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePlanLimits } from '@/hooks/use-plan-limits';

const REGION_OPTIONS = [
  { value: 'boston', label: 'Boston', flag: '🇺🇸', desc: 'US East' },
] as const;

const PROVISION_STEPS = [
  { key: 'validate', label: 'Validating credentials' },
  { key: 'create', label: 'Creating MetaApi account' },
  { key: 'deploy', label: 'Deploying to broker' },
] as const;

export default function AccountNewPage() {
  const navigate = useNavigate();
  const createAccount = useCreateMt5Account();
  const { data: accounts } = useMt5Accounts();

  const limits = usePlanLimits();
  const accountCount = accounts?.length ?? 0;
  const maxAccounts = limits.maxMt5Accounts;
  const atLimit = accountCount >= maxAccounts;

  const [loginId, setLoginId] = useState('');
  const [server, setServer] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [region, setRegion] = useState('boston');
  const [error, setError] = useState('');

  // Provisioning progress state
  const [provisioning, setProvisioning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const stepTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (stepTimer.current) clearInterval(stepTimer.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!loginId.trim()) {
      setError('Login ID is required.');
      return;
    }
    if (!server.trim()) {
      setError('Server name is required.');
      return;
    }
    if (!password) {
      setError('Password is required for MetaApi provisioning.');
      return;
    }

    // Start provisioning with step animation
    setProvisioning(true);
    setCurrentStep(0);

    // Advance steps on a timer to give visual feedback
    // Last step stays spinning until the API actually responds
    let step = 0;
    stepTimer.current = setInterval(() => {
      step++;
      if (step < PROVISION_STEPS.length - 1) {
        setCurrentStep(step);
      } else {
        setCurrentStep(PROVISION_STEPS.length - 1);
        if (stepTimer.current) clearInterval(stepTimer.current);
      }
    }, 2500);

    try {
      await createAccount.mutateAsync({
        login_id: loginId.trim(),
        server: server.trim(),
        password,
        name: name.trim() || undefined,
        region,
      });
      // Jump to final step
      if (stepTimer.current) clearInterval(stepTimer.current);
      setCurrentStep(PROVISION_STEPS.length);
      // Brief pause to show success, then redirect
      setTimeout(() => navigate('/accounts'), 600);
    } catch (err: unknown) {
      if (stepTimer.current) clearInterval(stepTimer.current);
      setProvisioning(false);
      setCurrentStep(0);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        'Failed to add account.';
      setError(typeof msg === 'string' ? msg : 'Failed to add account.');
    }
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Back Link */}
      <Link
        to="/accounts"
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        All Accounts
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center">
          <Server size={18} className="text-orange" />
        </div>
        <div>
          <h1 className="text-lg font-extrabold tracking-tight">Add MT5 Account</h1>
          <p className="text-xs text-ink-3">Link a MetaTrader 5 account via MetaApi</p>
        </div>
      </div>

      {/* Deriv broker suggestion */}
      <div className="flex items-start gap-2.5 bg-orange/5 border border-orange/15 rounded-lg px-4 py-3">
        <Info size={14} className="text-orange flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[11px] font-bold text-ink-2">Don't have an MT5 account yet?</p>
          <p className="text-[10px] text-ink-3 mt-0.5 leading-relaxed">
            We recommend Deriv — fast setup, low spreads, and works perfectly with ForgeAlpha.
          </p>
          <a
            href="https://partner-tracking.deriv.com/click?a=2204&o=1&c=3&link_id=1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-orange hover:text-orange/80 mt-1.5 transition-colors"
          >
            Open a free Deriv account
            <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Plan limit banner */}
      {atLimit && (
        <div className="flex items-start gap-2.5 bg-amber/5 border border-amber/15 rounded-lg px-4 py-3">
          <Lock size={14} className="text-amber flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[11px] font-bold text-amber">Account limit reached</p>
            <p className="text-[10px] text-amber/80 mt-0.5">
              Your beta plan allows {maxAccounts} MT5 account{maxAccounts !== 1 ? 's' : ''}.
              You currently have {accountCount}.
            </p>
          </div>
        </div>
      )}

      {/* Provisioning Progress Overlay */}
      {provisioning && (
        <div className="glass-sm rounded-lg p-5 border border-orange/20">
          <div className="flex items-center gap-2 mb-4">
            <Loader2 size={16} className="text-orange animate-spin" />
            <p className="text-sm font-bold">Setting up your account...</p>
          </div>
          <div className="space-y-2.5">
            {PROVISION_STEPS.map((step, i) => {
              const isDone = i < currentStep;
              const isActive = i === currentStep && currentStep < PROVISION_STEPS.length;
              const allDone = currentStep >= PROVISION_STEPS.length;
              return (
                <div key={step.key} className="flex items-center gap-2.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    isDone || allDone
                      ? 'bg-green/20 text-green'
                      : isActive
                        ? 'bg-orange/20 text-orange'
                        : 'bg-surface-2 text-ink-4'
                  }`}>
                    {isDone || allDone ? (
                      <Check size={11} />
                    ) : isActive ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <span className="text-[9px] font-bold">{i + 1}</span>
                    )}
                  </div>
                  <span className={`text-xs transition-colors duration-300 ${
                    isDone || allDone
                      ? 'text-green font-semibold'
                      : isActive
                        ? 'text-ink font-semibold'
                        : 'text-ink-4'
                  }`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
          {currentStep >= PROVISION_STEPS.length && (
            <p className="text-xs text-green font-semibold mt-3">
              Account linked! Balance and symbol data will sync in the background.
            </p>
          )}
        </div>
      )}

      {!provisioning && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ─── Section 1: Broker Credentials ─── */}
          <div className="glass-sm rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-1.5 mb-1">
              <KeyRound size={12} className="text-orange" />
              <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">
                Broker Credentials
              </p>
            </div>

            <Input
              label="Login ID *"
              placeholder="e.g., 12345678"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              required
            />

            <Input
              label="Password *"
              type="password"
              placeholder="MT5 password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <Input
              label="Server *"
              placeholder="e.g., ICMarketsSC-Demo"
              value={server}
              onChange={(e) => setServer(e.target.value)}
              required
            />

            <div className="flex items-start gap-2 bg-amber/5 border border-amber/15 rounded-lg px-3 py-2.5">
              <AlertCircle size={12} className="text-amber flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber/90 leading-relaxed">
                Your password is sent to MetaApi for provisioning only and is <span className="font-bold">never stored</span>.
              </p>
            </div>
          </div>

          {/* ─── Section 2: Display Settings ─── */}
          <div className="glass-sm rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Tag size={12} className="text-blue" />
              <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">
                Display Settings
              </p>
            </div>

            <Input
              label="Display Name"
              placeholder="e.g., My Demo Account (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* ─── Section 3: Region ─── */}
          <div className="glass-sm rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Globe size={12} className="text-orange" />
              <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">
                Engine Region
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {REGION_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRegion(r.value)}
                  className={`relative flex items-center gap-2.5 rounded-lg border px-3 py-2.5 transition-all duration-200 cursor-pointer ${
                    region === r.value
                      ? 'bg-orange/10 border-orange/40 shadow-[0_0_12px_rgba(249,115,22,0.1)]'
                      : 'bg-surface/50 border-glass-border hover:bg-surface hover:border-glass-border/80'
                  }`}
                >
                  <span className="text-lg leading-none">{r.flag}</span>
                  <div className="text-left">
                    <span className={`text-xs font-bold block ${region === r.value ? 'text-white' : 'text-ink-3'}`}>
                      {r.label}
                    </span>
                    <span className="text-[9px] text-ink-4">{r.desc}</span>
                  </div>
                  {region === r.value && (
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-orange" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ─── Auto-detected info ─── */}
          <div className="flex items-start gap-2 px-1">
            <Info size={11} className="text-ink-4 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-ink-4 leading-relaxed">
              Balance, leverage, and currency are automatically detected from your broker after provisioning.
            </p>
          </div>

          {/* ─── Error ─── */}
          {error && (
            <div className="flex items-center gap-2 bg-red/10 border border-red/20 rounded-lg px-3 py-2">
              <AlertCircle size={14} className="text-red flex-shrink-0" />
              <p className="text-[10px] text-red font-semibold">{error}</p>
            </div>
          )}

          {/* ─── Submit ─── */}
          <Button type="submit" className="w-full" disabled={atLimit}>
            {atLimit ? (
              <>
                <Lock size={14} />
                Account Limit Reached
              </>
            ) : (
              <>
                <Server size={14} />
                Add Account
              </>
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
